import { SessionTutorialBlocker } from '@/features/session-tutorial/SessionTutorialBlocker';
import { SessionTutorialDemoBadge } from '@/features/session-tutorial/SessionTutorialDemoBadge';
import { SessionTutorialHint } from '@/features/session-tutorial/SessionTutorialHint';
import { SessionTutorialOverlay } from '@/features/session-tutorial/SessionTutorialOverlay';
import { SessionTutorialSpotlight } from '@/features/session-tutorial/SessionTutorialSpotlight';
import type {
  SessionTutorialAnchorId,
  SessionTutorialContextValue,
  SessionTutorialProviderProps,
  SessionTutorialStepId,
} from '@/features/session-tutorial/sessionTutorial.types';
import {
  ACTION_STEPS,
  DESKTOP_STEPS,
  MOBILE_STEPS,
  READ_ONLY_STEPS,
  sessionTutorialContent,
  sessionTutorialNav,
} from '@/features/session-tutorial/sessionTutorialContent';
import {
  DEMO_LEAD_IN_MS,
  DEMO_LINE_INTERVAL_MS,
  DEMO_SETTLE_MS,
  sessionTutorialDemoScript,
} from '@/features/session-tutorial/sessionTutorialDemo';
import { useInertOutside } from '@/features/session-tutorial/useInertOutside';
import { useIsMobile } from '@/hooks/use-mobile';
import { usePiiReminder } from '@/hooks/usePiiReminder';
import { useSettings } from '@/hooks/useSettings';
import { createContext, useCallback, useContext, useEffect, useLayoutEffect, useRef, useState } from 'react';

type AnchorRegistry = ReadonlyMap<SessionTutorialAnchorId, readonly HTMLElement[]>;
type UnavailableReports = ReadonlyMap<SessionTutorialStepId, number>;

const withAnchor = (registry: AnchorRegistry, step: SessionTutorialAnchorId, element: HTMLElement, add: boolean) => {
  const others = (registry.get(step) ?? []).filter((existing) => existing !== element);
  return new Map(registry).set(step, add ? [...others, element] : others);
};

const withStep = (steps: ReadonlySet<SessionTutorialStepId>, step: SessionTutorialStepId) =>
  steps.has(step) ? steps : new Set(steps).add(step);

const withReport = (reports: UnavailableReports, step: SessionTutorialStepId, delta: number) =>
  new Map(reports).set(step, (reports.get(step) ?? 0) + delta);

/** Session pages keep CSS-hidden copies of their controls: only a rendered one can be pointed at. */
const isRendered = (element: HTMLElement) => element.isConnected && element.getClientRects().length > 0;

const hasSameMembers = (a: ReadonlySet<HTMLElement>, b: ReadonlySet<HTMLElement>) =>
  a.size === b.size && [...a].every((element) => b.has(element));

const SessionTutorialContext = createContext<SessionTutorialContextValue | null>(null);

/** Null unless the tutorial is pending here; anchors then render their child unchanged. */
export const useSessionTutorial = () => useContext(SessionTutorialContext);

export const SessionTutorialProvider = ({ mode, enabled, paused, demo, children }: SessionTutorialProviderProps) => {
  const { features, isReady, setFeatures } = useSettings({ enabled });
  const { showReminder } = usePiiReminder();
  const isMobile = useIsMobile();
  const [anchors, setAnchors] = useState<AnchorRegistry>(() => new Map());
  // Anchors with layout boxes, measured after React applies an update and again whenever one is resized:
  // a CSS-only show/hide (e.g. the `hidden md:flex` mobile views) re-renders nothing.
  const [renderedAnchors, setRenderedAnchors] = useState<ReadonlySet<HTMLElement>>(() => new Set());
  const [doneSteps, setDoneSteps] = useState<ReadonlySet<SessionTutorialStepId>>(() => new Set());
  // Mounted reports that a step's control will not appear (e.g. no templates), counted per reporter.
  const [unavailableReports, setUnavailableReports] = useState<UnavailableReports>(() => new Map());
  // Sample lines played into the page so far; 0 once cleared, or abandoned for a real recording.
  const [demoLinesShown, setDemoLinesShown] = useState(0);
  // The page passes new callbacks each render; the playback timer must not restart with them.
  const demoRef = useRef(demo);
  demoRef.current = demo;

  const script = sessionTutorialDemoScript[mode];
  const isShown = (element: HTMLElement) => renderedAnchors.has(element);
  const findShown = (anchor: SessionTutorialAnchorId) => anchors.get(anchor)?.find(isShown);
  const hasRenderedAnchor = (anchor: SessionTutorialAnchorId) => !!findShown(anchor);
  // Left out, not waited on, unless another copy of the page (e.g. a still-mounted hidden panel) shows it.
  const isUnavailable = (step: SessionTutorialStepId) =>
    (unavailableReports.get(step) ?? 0) > 0 && !hasRenderedAnchor(step);
  const steps = (isMobile ? MOBILE_STEPS : DESKTOP_STEPS).filter((step) => !isUnavailable(step));
  const tipSteps = steps.filter((step) => step !== 'demo');
  const currentStep = steps.find((step) => !doneSteps.has(step)) ?? null;
  // Everyone else (done, never scheduled, resumed session) mounts no anchors or overlay at all.
  const isPending = enabled && isReady && features.session_tutorial === 'pending';
  // The daily PII reminder is modal: wait until it is acknowledged.
  const activeStep = isPending && !paused && !showReminder ? currentStep : null;
  // A step whose control is hidden right now (e.g. the other mobile view) waits until it is shown again.
  const target = activeStep ? findShown(activeStep) : undefined;

  const finish = () => {
    if (demoLinesShown > 0) demoRef.current.clear();
    setDemoLinesShown(0);
    setFeatures({ session_tutorial: 'done' });
  };
  const next = () => {
    if (!currentStep) return;
    if (currentStep === steps[steps.length - 1]) finish();
    else setDoneSteps((done) => withStep(done, currentStep));
  };
  const complete = (step: SessionTutorialStepId) => {
    if (step === activeStep) next();
  };

  useLayoutEffect(() => {
    const elements = [...anchors.values()].flat();
    const measure = () => {
      const shown = new Set(elements.filter(isRendered));
      setRenderedAnchors((current) => (hasSameMembers(current, shown) ? current : shown));
    };
    measure();
    const observer = new ResizeObserver(measure);
    for (const element of elements) observer.observe(element);
    return () => observer.disconnect();
  }, [anchors]);

  const register = useCallback((step: SessionTutorialAnchorId, element: HTMLElement) => {
    setAnchors((registry) => withAnchor(registry, step, element, true));
    return () => setAnchors((registry) => withAnchor(registry, step, element, false));
  }, []);
  const markUnavailable = useCallback((step: SessionTutorialStepId) => {
    setUnavailableReports((reports) => withReport(reports, step, 1));
    return () => setUnavailableReports((reports) => withReport(reports, step, -1));
  }, []);

  // A real recording replaces the demo: take the sample (and any note from it) off the page first,
  // and never play more of it in.
  const hasDemoContent = demoLinesShown > 0;
  useEffect(() => {
    if (!paused) return;
    if (hasDemoContent) demoRef.current.clear();
    setDoneSteps((done) => withStep(done, 'demo'));
    setDemoLinesShown(0);
  }, [paused, hasDemoContent]);

  // The tour stops mid-demo for any other reason (switched off here, or finished in another tab and
  // picked up by a settings refresh): same, the sample never outlives the tour.
  useEffect(() => {
    if (isPending || !hasDemoContent) return;
    demoRef.current.clear();
    setDemoLinesShown(0);
  }, [isPending, hasDemoContent]);

  // Every step left was dropped (e.g. no template, so no note can be made): the tour is over.
  const isOutOfSteps = isPending && doneSteps.size > 0 && !currentStep;
  const finishRef = useRef(finish);
  finishRef.current = finish;
  useEffect(() => {
    if (isOutOfSteps) finishRef.current();
  }, [isOutOfSteps]);

  // The generate step is done when the note is actually there; a failed generation keeps it open.
  const hasNote = hasRenderedAnchor('result');
  useEffect(() => {
    if (activeStep === 'summary' && hasNote) setDoneSteps((done) => withStep(done, 'summary'));
  }, [activeStep, hasNote]);

  // Play the sample transcript into the page: a short lead-in with the "talrit mun birtast hér" hint,
  // then one line at a time, and a pause on the finished transcript before moving on.
  useEffect(() => {
    if (activeStep !== 'demo') return;
    const isFinished = demoLinesShown >= script.length;
    const delay = isFinished ? DEMO_SETTLE_MS : demoLinesShown === 0 ? DEMO_LEAD_IN_MS : DEMO_LINE_INTERVAL_MS;
    const timeout = setTimeout(() => {
      if (isFinished) {
        setDoneSteps((done) => withStep(done, 'demo'));
        return;
      }
      demoRef.current.showLine(script[demoLinesShown], demoLinesShown);
      setDemoLinesShown(demoLinesShown + 1);
    }, delay);
    return () => clearTimeout(timeout);
  }, [activeStep, demoLinesShown, script]);

  const value = isPending ? { activeStep, register, markUnavailable, complete } : null;
  const tipStep = activeStep === 'demo' ? null : activeStep;
  const isPointing = !!tipStep && !!target;
  // No tip, but something worth seeing: the sample transcript filling in, or the note being written.
  const litAnchor = activeStep === 'demo' ? 'demo' : activeStep === 'summary' && !target ? 'generating' : null;
  const litTarget = !isPointing && litAnchor ? findShown(litAnchor) : undefined;
  // Before the first sample line: point at where the transcript will appear (the empty conversation
  // transcript, or the dictation card), until that line replaces the hint.
  const isListening = activeStep === 'demo' && demoLinesShown === 0;
  const listeningTarget = isListening ? (findShown('listening') ?? litTarget) : undefined;
  // Only the highlighted control can be used (mouse and keyboard); on a read-only step, or while the
  // sample plays or its note is written, nothing on the page can, so the sample can never be edited,
  // saved or mixed with real work.
  const usableTarget = isPointing && tipStep && !READ_ONLY_STEPS.has(tipStep) ? (target ?? null) : null;
  // The sample step counts from the click on record, before its first line is on the page.
  const isDemoUnderway = hasDemoContent || activeStep === 'demo';
  useInertOutside(usableTarget, isPending && (isPointing || isDemoUnderway));

  return (
    <SessionTutorialContext.Provider value={value}>
      {children}
      {isPending && hasDemoContent && <SessionTutorialDemoBadge onSkip={finish} />}
      {/* Nothing to point at while the sample plays or its note is generated: keep the page out of reach. */}
      {isPending && listeningTarget && (
        <SessionTutorialHint target={listeningTarget} text={sessionTutorialNav.listeningHint} />
      )}
      {isPending && !listeningTarget && litTarget && <SessionTutorialSpotlight target={litTarget} />}
      {isPending && isDemoUnderway && !isPointing && !litTarget && !listeningTarget && <SessionTutorialBlocker />}
      {tipStep && target && (
        <SessionTutorialOverlay
          target={target}
          copy={sessionTutorialContent[mode][tipStep]}
          stepNumber={tipSteps.indexOf(tipStep) + 1}
          totalSteps={tipSteps.length}
          canAdvance={!ACTION_STEPS.has(tipStep)}
          onNext={next}
          onSkip={finish}
        />
      )}
    </SessionTutorialContext.Provider>
  );
};
